import { useEffect, useState } from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

function RequirementModal({ show, onClose, requirement }) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [coverageTestCases, setCoverageTestCases] = useState([]);

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });
  const statusLabels = {
    raw: "Raw",
    review: "Review",
    clarified: "Clarified",
    needs_clarification: "Needs Clarification",
    targeted: "Targeted",
    implemented: "Implemented"
  };
  const fieldLabels = {
    nodeType: "Node type",
    number: "Number",
    name: "Name",
    note: "Note",
    quote: "Quote",
    type: "Type",
    sil: "SIL",
    class: "Class",
    coverage: "Coverage",
    status: "Status",
    targetVersionId: "Target",
    userId: "User"
  };

  useEffect(() => {
    if (!show || !requirement?.id || !showHistory) return;

    let cancelled = false;

    const loadHistory = async () => {
      setLoadingHistory(true);

      try {
        const res = await fetchWithAuth(`/api/requirements/${requirement.id}/history`);

        if (!res.ok) {
          if (!cancelled) {
            setHistory([]);
          }
          return;
        }

        const data = await res.json();

        if (!cancelled) {
          setHistory(data);
        }
      } catch (err) {
        if (!cancelled) {
          setHistory([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingHistory(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [show, requirement, showHistory]);

  useEffect(() => {
    if (!show) {
      setShowHistory(false);
      setHistory([]);
      setLoadingHistory(false);
      setCoverageTestCases([]);
    }
  }, [show]);

  useEffect(() => {
    const coverageIds = Array.isArray(requirement?.coverage)
      ? requirement.coverage.map((id) => Number(id))
      : [];

    if (!show || coverageIds.length === 0) {
      setCoverageTestCases([]);
      return;
    }

    let cancelled = false;

    const loadTestCases = async () => {
      try {
        const res = await fetchWithAuth("/api/test-cases?compact=1");

        if (!res.ok) {
          if (!cancelled) {
            setCoverageTestCases([]);
          }
          return;
        }

        const data = await res.json();
        const coverageIdSet = new Set(coverageIds);

        if (!cancelled) {
          setCoverageTestCases(data.filter((testCase) => coverageIdSet.has(Number(testCase.id))));
        }
      } catch (err) {
        if (!cancelled) {
          setCoverageTestCases([]);
        }
      }
    };

    loadTestCases();

    return () => {
      cancelled = true;
    };
  }, [show, requirement]);

  const formatStatusLabel = (value) => statusLabels[value] ?? value ?? "";
  const getActionBadge = (action) => {
    if (action === "create") {
      return <Badge bg="success">Create</Badge>;
    }

    if (action === "update") {
      return <Badge bg="primary">Update</Badge>;
    }

    if (action === "delete") {
      return <Badge bg="danger">Delete</Badge>;
    }

    return <Badge bg="secondary">{action}</Badge>;
  };

  const formatChangeValue = (field, value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (field === "status") {
      return formatStatusLabel(value);
    }

    if (field === "targetVersionId") {
      if (requirement.TargetVersion && Number(value) === requirement.TargetVersion.id) {
        return requirement.TargetVersion.name;
      }

      return `Version #${value}`;
    }

    if (field === "coverage" && Array.isArray(value)) {
      return value.length ? value.join(", ") : "-";
    }

    return String(value);
  };

  const renderCoverage = () => {
    const coverageIds = Array.isArray(requirement.coverage) ? requirement.coverage : [];

    if (!coverageIds.length) {
      return "";
    }

    if (!coverageTestCases.length) {
      return coverageIds.join(", ");
    }

    return (
      <div className="d-flex flex-column gap-1">
        {coverageTestCases.map((testCase) => (
          <div key={testCase.id}>
            <span className="fw-semibold">{testCase.number}</span>
            <span className="text-muted"> - {testCase.name}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderSystemRequirements = () => {
    const systemRequirements = requirement.SystemRequirements ?? [];

    if (!systemRequirements.length) return "";

    return (
      <div className="d-flex flex-column gap-1">
        {systemRequirements.map((systemRequirement) => (
          <div key={systemRequirement.id}>
            <span className="fw-semibold">{systemRequirement.number}</span>
            <span className="text-muted"> - {systemRequirement.name}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderHistoryChanges = (changes) => {
    const entries = Object.entries(changes ?? {});

    if (!entries.length) {
      return <span className="text-muted">-</span>;
    }

    return entries.map(([field, change]) => {
      const isChangePair = (
        change !== null &&
        typeof change === "object" &&
        !Array.isArray(change) &&
        (Object.prototype.hasOwnProperty.call(change, "old") || Object.prototype.hasOwnProperty.call(change, "new"))
      );

      return (
        <div key={field}>
          <strong>{fieldLabels[field] ?? field}</strong>: {isChangePair ? (
            <>
              {formatChangeValue(field, change.old)} {"->"} {formatChangeValue(field, change.new)}
            </>
          ) : (
            formatChangeValue(field, change)
          )}
        </div>
      );
    });
  };

  if (!requirement) return null;

  return (
    <Modal show={show} onHide={onClose} size={showHistory ? "xl" : "lg"}>

      <Modal.Header closeButton>
        <Modal.Title>
          Requirement {requirement.number}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>

        <table className="table">
          <tbody>

            <tr style={getRowStyle(0)}>
              <th>Name</th>
              <td>{requirement.name}</td>
            </tr>

            <tr style={getRowStyle(1)}>
              <th>Type</th>
              <td>{requirement.type}</td>
            </tr>

            <tr style={getRowStyle(2)}>
              <th>SIL</th>
              <td>{requirement.sil}</td>
            </tr>

            <tr style={getRowStyle(3)}>
              <th>Class</th>
              <td>{requirement.class}</td>
            </tr>

            <tr style={getRowStyle(4)}>
              <th>Coverage</th>
              <td>{renderCoverage()}</td>
            </tr>

            <tr style={getRowStyle(5)}>
              <th>System Requirements</th>
              <td>{renderSystemRequirements()}</td>
            </tr>

            <tr style={getRowStyle(6)}>
              <th>Note</th>
              <td>{requirement.note}</td>
            </tr>

            <tr style={getRowStyle(7)}>
              <th>Quote</th>
              <td style={{ whiteSpace: "pre-wrap" }}>{requirement.quote}</td>
            </tr>

            <tr style={getRowStyle(8)}>
              <th>Status</th>
              <td>{statusLabels[requirement.status] ?? requirement.status ?? ""}</td>
            </tr>

            <tr style={getRowStyle(9)}>
              <th>Target</th>
              <td>{requirement.TargetVersion?.name ?? ""}</td>
            </tr>

            <tr style={getRowStyle(10)}>
              <th>User</th>
              <td>{requirement.User?.name}</td>
            </tr>

          </tbody>
        </table>

        <div className="d-flex justify-content-end">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowHistory((current) => !current)}
          >
            {showHistory ? "Hide history" : "Show history"}
          </Button>
        </div>

        {showHistory && (
          <div className="mt-4">
            <h6 className="mb-3">History</h6>

            {loadingHistory ? (
              <div>Loading...</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.92rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "180px" }}>When</th>
                      <th style={{ width: "120px" }}>Who</th>
                      <th style={{ width: "90px" }}>Action</th>
                      <th>Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          No history found.
                        </td>
                      </tr>
                    ) : history.map((entry, index) => (
                      <tr key={entry.id} style={getRowStyle(index)}>
                        <td>{new Date(entry.createdAt).toLocaleString()}</td>
                        <td>{entry.User?.name ?? "-"}</td>
                        <td>{getActionBadge(entry.action)}</td>
                        <td>{renderHistoryChanges(entry.changes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>

    </Modal>
  );
}

export default RequirementModal;
